import { useAuth } from '@praxis/component-auth'
import { ErrorBoundary, LogLevel } from '@praxis/component-logging'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import { Layout, Button, Drawer, Grid } from '@enterprise-ui/canvas-ui-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { trackLogOut } from '../common/fireflyEvents'
import {
  getExternalVendorDetails,
  getFromLocalStorage,
  getLoggedInUserId,
  getSelectedAccount,
  getUserEmail,
  isCrossAdvertiserAccount,
  isPOLDomain,
  isPOLSessionActive,
  removeLocalStorageItems,
} from '../common/helper'
import FallbackComponent from '../components/ErrorBoundary/FallBackScreen'
import TimeoutScreen from '../components/ErrorBoundary/TimeoutScreen'
import Header from '../components/Header'
import Spinner from '../components/Spinner'
import Routes from '../routes'
import UniversalAlert from '../components/UniversalAlert'
import { useState, useEffect, useCallback } from 'react'
import useGetUniversalStatus from '../domain/useUniversalStatus'
import { FeedbackBlock, StyledLayoutBody } from './style'
import FeedbackForm from '../components/Header/FeedbackForm'
import Toast from '../components/Toast'
import { trackEvent } from '../common/fireflyHelper'
import { trackError, trackPageView } from '../common/otel'
import { SideNav } from '../components/SideNav'
import useUserType from '../domain/useUserType'
import {
  DashboardIcon,
  StarIcon,
  ClockIcon,
  CalculationIcon,
  TroubleshootIcon,
  DataTableIcon,
  PlusCircleIcon,
  DatabaseIcon,
} from '@enterprise-ui/icons'
import {
  POL_DEV_SUPPORT_LINK,
  POL_PROD_SUPPORT_LINK,
  TERMS_OF_USE_DEFAULT_LINK,
} from '../config/appConfig'
import Footer from '../components/Footer'
import { log } from '../common/log'
import {
  isAdmin,
  isLoggedInAsInternalUser,
  isRmsUser,
} from '../common/userAccessHelpers'
import SurveyForm from '../components/SurveyForm'
import useWhatsNewTour from '../domain/useWhatsNewTour'
import { useGuidedTour } from '../hooks/useGuidedTours'
import Joyride, { CallBackProps } from 'react-joyride'
import useIsFeatureFlagEnabled from '../hooks/FeatureFlag/useIsFeatureFlagEnabled'
import useFeatureFlagState from '../hooks/FeatureFlag/useFeatureFlagState'
import {
  FEATURE_EVENT_DATE_TABLE_RESTRICTION,
  FEATURE_GCP_MIGRATION_ENABLED,
  FEATURE_NEW_DASHBOARD,
  FEATURE_WHATS_NEW,
} from '../constants/featureFlags'
import GlobalWhatsNew from '../components/WhatsNew/Global'
import { ArrowLeftIcon } from '@design-systems/react-icons'
interface Props {
  apiConfig: any
}

const DefaultLayout = (props: Props) => {
  const {
    isEnabled: isNewDashboardEnabled,
    isLoading: isNewDashboardFlagLoading,
  } = useFeatureFlagState(FEATURE_NEW_DASHBOARD)
  const isWhatsNewFeatureFlagEnabled =
    useIsFeatureFlagEnabled(FEATURE_WHATS_NEW)
  useIsFeatureFlagEnabled(FEATURE_EVENT_DATE_TABLE_RESTRICTION)
  useIsFeatureFlagEnabled(FEATURE_GCP_MIGRATION_ENABLED)

  const { data: whatsNewTourResponse } = useWhatsNewTour(
    isWhatsNewFeatureFlagEnabled,
  )

  const tours = isWhatsNewFeatureFlagEnabled
    ? (whatsNewTourResponse?.popovers ?? [])
    : []

  const handleJoyrideComplete = useCallback(() => {
    setIsJoyrideComplete(true)
  }, [])

  const { joyrideProps } = useGuidedTour(tours, handleJoyrideComplete) || {
    joyrideProps: {},
  }
  const [isGlobalWhatsNewOpen, setIsGlobalWhatsNewOpen] = useState(true)
  const [isJoyrideComplete, setIsJoyrideComplete] = useState(false)
  const [isCrossAdvModalComplete, setIsCrossAdvModalComplete] = useState(false)
  const { apiConfig } = props
  const history = useNavigate()
  const [hideAlert, setHideAlert] = useState<boolean>(false)
  const [isSideNavMinimized, setIsSideNavMinimized] = useState<boolean>(false)
  const isFetching = useIsFetching()
  const isMutating = useIsMutating()
  const auth = useAuth()
  const location = useLocation()
  const isAuthenticated = auth.isAuthenticated()
  const { data: userDetails } = useUserType()
  const { user_type } = userDetails || {}
  const [isFeedbackFormVisible, setFeedbackFormVisibility] =
    useState<boolean>(false)

  const { data: alertData } = useGetUniversalStatus('UNIVERSAL_ALERT')
  const [feedbackToast, showFeedbackSubmitToast] = useState<string>('')

  let alertMsg = alertData?.params?.status ? alertData?.content : ''

  const isCrossAdvertiser = isCrossAdvertiserAccount(apiConfig)

  const handleJoyrideCallback = (data: CallBackProps) => {
    joyrideProps?.callback?.(data)
  }

  const isDashboardRoute =
    location.pathname === '/' ||
    location.pathname.startsWith('/b/') ||
    location.pathname.startsWith('/v/')

  const [useNewDashboardPref, setUseNewDashboardPref] = useState<boolean>(
    () => getFromLocalStorage('useNewDashboard') !== false,
  )

  useEffect(() => {
    const handler = (e: CustomEvent<{ useNewDashboard: boolean }>) => {
      setUseNewDashboardPref(e.detail.useNewDashboard)
    }
    const storageHandler = (e: StorageEvent) => {
      if (e.key === 'useNewDashboard') {
        setUseNewDashboardPref(e.newValue === 'true')
      }
    }
    window.addEventListener('dashboardToggle', handler as EventListener)
    window.addEventListener('storage', storageHandler)
    return () => {
      window.removeEventListener('dashboardToggle', handler as EventListener)
      window.removeEventListener('storage', storageHandler)
    }
  }, [])

  const isOnNewDashboard =
    isDashboardRoute && isNewDashboardEnabled && useNewDashboardPref

  const isDashboardFlagResolving = isDashboardRoute && isNewDashboardFlagLoading

  const crossAdvModalApplies = isOnNewDashboard && isCrossAdvertiser

  // Listen for cross-adv modal completion event from NewDashboard
  useEffect(() => {
    const handleComplete = () => setIsCrossAdvModalComplete(true)
    window.addEventListener('cross-adv-modal-complete', handleComplete)
    return () =>
      window.removeEventListener('cross-adv-modal-complete', handleComplete)
  }, [])

  const isWhatsNewSequenceComplete =
    !isGlobalWhatsNewOpen &&
    (isCrossAdvModalComplete || !crossAdvModalApplies) &&
    (isJoyrideComplete ||
      !isWhatsNewFeatureFlagEnabled ||
      (!!whatsNewTourResponse && tours.length === 0))

  if (!isAuthenticated) {
    removeLocalStorageItems([
      'userDir',
      'userADGroups',
      'selected_vendor',
      'external_vendor_detail',
    ])
  }

  const callListenerForCompanyChange = () => {
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) {
        const { session } = auth
        const userId = session?.userInfo?.lanId || ''
        const isActiveSession = isPOLSessionActive(userId)
        if (!isActiveSession) {
          trackLogOut()
          removeLocalStorageItems(['selected_vendor', 'external_vendor_detail'])
          auth.logout()
        }
      }
    })
  }

  const handleClick = () => {
    setHideAlert(true)
  }

  callListenerForCompanyChange()

  useEffect(() => {
    showFeedbackSubmitToast('')
    // Emit an OTel page-view metric on each route change. Safe no-op until
    // OTel is initialized.
    trackPageView(location.pathname)
  }, [location])

  const isNavSelected = (path: string) => {
    return location.pathname === path
  }

  const newReportRedirect = () => {
    trackEvent('New Report: Clicked on SideNav')
    history('/view')
  }

  const getRmsURL = () => {
    if (isPOLDomain(window.location.origin)) {
      return apiConfig?.rmsPolURL
    } else {
      return apiConfig?.rmsEnterpriseURL
    }
  }

  const termsOfUseLink = () => {
    if (user_type === 'EXTERNAL_TARGET_PLUS') {
      return `${window.location.origin}/termsOfUse`
    } else if (isRmsUser(apiConfig)) {
      return `${getRmsURL()}${apiConfig?.supportURLs?.RMS?.termsOfUseLink}`
    } else {
      const domain =
        apiConfig.appEnv === 'prod'
          ? POL_PROD_SUPPORT_LINK
          : POL_DEV_SUPPORT_LINK
      const termsOfuse =
        apiConfig?.supportURLs?.POL?.termsOfUseLink || TERMS_OF_USE_DEFAULT_LINK
      return `${domain}${termsOfuse}`
    }
  }

  const btnStyles = {
    border: '1px solid',
    marginTop: '10px',
    marginBottom: '10px',
    borderRadius: '8px',
    height: '36px',
  }

  const backToDashboardStyles = {
    marginBottom: '10px',
    padding: '16px',
    borderBottom: `1px solid var(--color-border-subdued)`,
    color: 'var(--canvas-background-color--interactive)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }

  let sideNavMenuList = [
    {
      label: 'New Report',
      selected: false,
      icon: PlusCircleIcon,
      as: Button,
      otherProps: {
        type: 'secondary',
        style: btnStyles,
        onClick: newReportRedirect,
      },
    },
    {
      label: 'Dashboard',
      to: '/',
      selected: isNavSelected('/'),
      icon: DashboardIcon,
      as: Link,
      isReactLink: true,
    },
    {
      label: 'Saved Views',
      to: '/views',
      selected: isNavSelected('/views'),
      icon: StarIcon,
      as: Link,
      isReactLink: true,
    },
    {
      label: 'Templates',
      to: '/templates',
      selected: isNavSelected('/templates'),
      icon: DataTableIcon,
      as: Link,
      isReactLink: true,
    },
    {
      label: 'Scheduled Reports',
      to: '/scheduledReports',
      selected: isNavSelected('/scheduledReports'),
      icon: ClockIcon,
      as: Link,
      isReactLink: true,
    },
    {
      label: 'Metrics and Dimensions',
      to: '/manageMetrics',
      selected: isNavSelected('/manageMetrics'),
      icon: CalculationIcon,
      as: Link,
      isReactLink: true,
    },
  ]

  let newDashboardSideNavMenuList = [
    {
      label: 'Back to Dashboard',
      to: '/',
      selected: false,
      icon: ArrowLeftIcon,
      as: Link,
      isReactLink: true,
      otherProps: {
        style: backToDashboardStyles,
      },
    },
    {
      label: 'Create New Report',
      selected: isNavSelected('/view'),
      icon: PlusCircleIcon,
      otherProps: {
        onClick: newReportRedirect,
      },
    },
    {
      label: 'Saved Views',
      to: '/views',
      selected: isNavSelected('/views'),
      icon: StarIcon,
      as: Link,
      isReactLink: true,
    },
    {
      label: 'Templates',
      to: '/templates',
      selected: isNavSelected('/templates'),
      icon: DataTableIcon,
      as: Link,
      isReactLink: true,
    },
    {
      label: 'Scheduled Reports',
      to: '/scheduledReports',
      selected: isNavSelected('/scheduledReports'),
      icon: ClockIcon,
      as: Link,
      isReactLink: true,
    },
    {
      label: 'Metrics and Dimensions',
      to: '/manageMetrics',
      selected: isNavSelected('/manageMetrics'),
      icon: CalculationIcon,
      as: Link,
      isReactLink: true,
    },
  ]

  if (isLoggedInAsInternalUser()) {
    sideNavMenuList.push({
      label: isAdmin(apiConfig) ? 'Admin' : 'Mapping Info',
      to: '/admin',
      selected: isNavSelected('/admin'),
      icon: isAdmin(apiConfig) ? TroubleshootIcon : DatabaseIcon,
      as: Link,
      isReactLink: true,
    })
    newDashboardSideNavMenuList.push({
      label: isAdmin(apiConfig) ? 'Admin' : 'Mapping Info',
      to: '/admin',
      selected: isNavSelected('/admin'),
      icon: isAdmin(apiConfig) ? TroubleshootIcon : DatabaseIcon,
      as: Link,
      isReactLink: true,
    })
  }

  let selectedAcct: SelectOption
  if (
    !['EXTERNAL_OTHERS', 'EXTERNAL_RMS', 'EXTERNAL_TARGET_PLUS'].includes(
      user_type,
    )
  ) {
    selectedAcct = getSelectedAccount()
  } else {
    selectedAcct = getExternalVendorDetails()
  }

  return (
    <ErrorBoundary
      key={location.pathname}
      FallbackComponent={(errorObject: any) => {
        const errorMessage = errorObject?.error?.message

        const finalErrorMessage = `Reports UI Error = ${errorMessage}, for the user ${getLoggedInUserId()} (${getUserEmail()}). Selected account is ${selectedAcct?.id} (${selectedAcct?.value}).`
        log.error({ message: finalErrorMessage })

        // Emit an OTel error counter so frontend crashes are observable in
        // the metrics pipeline. Safe no-op until OTel is initialized.
        trackError('error-boundary', { route: location.pathname })

        if (LogLevel.Info) {
          return (
            <FallbackComponent userDetails={userDetails}></FallbackComponent>
          )
        } else if (LogLevel.Error) {
          return <TimeoutScreen userDetails={userDetails}></TimeoutScreen>
        } else {
          return (
            <FallbackComponent userDetails={userDetails}></FallbackComponent>
          )
        }
      }}
    >
      {/* New header v2 code */}
      <Layout theme="roundel" fullWidth={true} darkMode={false}>
        {isAuthenticated && <Header userDetails={userDetails} />}
        {isAuthenticated &&
          alertMsg &&
          !hideAlert &&
          isWhatsNewSequenceComplete && (
            <UniversalAlert data={alertMsg} handleClick={handleClick} />
          )}

        <StyledLayoutBody
          includeRail={true}
          isSideNavMinimized={isSideNavMinimized}
          isOnNewDashboard={isOnNewDashboard}
        >
          {isWhatsNewFeatureFlagEnabled &&
            !isGlobalWhatsNewOpen &&
            (isCrossAdvModalComplete || !crossAdvModalApplies) && (
              <Joyride {...joyrideProps} callback={handleJoyrideCallback} />
            )}
          <Grid.Container justify="center">
            {!isOnNewDashboard && !isDashboardFlagResolving && (
              <Grid.Item xs={2} className="first_column">
                {isAuthenticated && (
                  <SideNav
                    items={
                      isNewDashboardEnabled && useNewDashboardPref
                        ? newDashboardSideNavMenuList
                        : sideNavMenuList
                    }
                    settings={{
                      canMinimize: true,
                      hasOverlay: false,
                      isMinimized: isSideNavMinimized,
                      onMinimizeChange: (isMinimized: any) => {
                        setIsSideNavMinimized(isMinimized)
                      },
                    }}
                  />
                )}
              </Grid.Item>
            )}
            <Grid.Item xs className={'second_column'}>
              <Routes userDetails={userDetails} />
              {isAuthenticated && (
                <Footer termAndConditionsUrl={termsOfUseLink()}>
                  <Button
                    className="openDrawer"
                    type="primary"
                    onClick={() => {
                      setFeedbackFormVisibility(true)
                      showFeedbackSubmitToast('')
                    }}
                  >
                    Feedback
                  </Button>
                </Footer>
              )}
              {isAuthenticated && selectedAcct && <SurveyForm />}
              {
                <FeedbackBlock>
                  <Drawer
                    isVisible={isFeedbackFormVisible}
                    onRequestClose={() => {
                      setFeedbackFormVisibility(false)
                      showFeedbackSubmitToast('')
                    }}
                    headingText="How would you like to rate us?"
                  >
                    <FeedbackForm
                      isVisible={isFeedbackFormVisible}
                      onCancel={() => {
                        setFeedbackFormVisibility(false)
                      }}
                      onSubmit={(type: string) => {
                        showFeedbackSubmitToast(type)
                        trackEvent(`Feedback: Submit`, {
                          key: `Feedback Form`,
                          module: 'Layout',
                        })
                      }}
                      from={'popup'}
                    />
                  </Drawer>
                </FeedbackBlock>
              }
              {feedbackToast === 'success' && (
                <Toast
                  type="success"
                  message="Feedback submitted successfully."
                />
              )}
              {feedbackToast === 'error' && (
                <Toast
                  type="error"
                  message="Something went wrong! Please try again."
                />
              )}
            </Grid.Item>
          </Grid.Container>
        </StyledLayoutBody>
        {isGlobalWhatsNewOpen && (
          <GlobalWhatsNew
            onModalClose={() => {
              setIsGlobalWhatsNewOpen(false)
              ;(window as any).__globalWhatsNewClosed = true
              window.dispatchEvent(new Event('global-whatsnew-closed'))
            }}
          />
        )}
      </Layout>
      {/* End of New header v2 code */}
      {!isOnNewDashboard && isFetching + isMutating > 0 && <Spinner />}
    </ErrorBoundary>
  )
}

export default DefaultLayout
